import { FOLLOW_UP_OFFSET_DAYS, computeFollowUpWindow } from '@qualicloud/domain';
import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Dialog,
  Field,
  IconAlertTriangle,
  IconInfo,
  IconUsers,
  RadioGroup,
  Textarea,
} from '@/design-system';
import { formatRegulatedDateTime } from '@/lib/format/datetime';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * VERIFICAÇÃO DE EFICÁCIA — com o aviso do follow-up ANTES da confirmação
 * sgq.md §C.5 e §B.8
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * REPROVAR NÃO É SÓ REPROVAR.
 *
 * O servidor cria, na MESMA transação, uma SACAP de follow-up para D+3 às 09:00
 * no fuso da organização, herda as não-conformidades e os participantes da
 * reunião de origem, MOVE a ação para a nova reunião e a reabre. No legado isso
 * acontecia em silêncio — e dois dos sete passos falhavam SEMPRE, sem ninguém
 * saber: a SACAP de follow-up nascia sem tipo (BUG-REU-02) e sem participantes
 * (BUG-REU-03), de modo que ela nunca chegava a "aguardando eficácia" e não
 * tinha quem comparecesse.
 *
 * A data exibida vem de `computeFollowUpWindow` — a MESMA função de
 * `@qualicloud/domain` que o servidor executa, inclusive o desvio de fim de
 * semana. Não é estimativa da tela: é a regra, e o usuário vê exatamente a
 * reunião que será criada.
 */

export interface EffectivenessDecision {
  readonly approved: boolean;
  readonly justification: string;
  readonly evidenceSummary: string;
}

export interface EffectivenessDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Fuso da organização. A janela do follow-up é calculada nele, não no do browser. */
  readonly timeZone: string;
  readonly assigneeName: string | null;
  readonly requesterName: string | null;
  /** Injetável para teste determinístico. Default: agora. */
  readonly now?: Date;
  readonly onConfirm: (decision: EffectivenessDecision) => void;
}

export function EffectivenessDialog({
  open,
  onOpenChange,
  timeZone,
  assigneeName,
  requesterName,
  now,
  onConfirm,
}: EffectivenessDialogProps) {
  const [resolved, setResolved] = useState<'yes' | 'no'>('yes');
  const [justification, setJustification] = useState('');
  const [evidenceSummary, setEvidenceSummary] = useState('');

  useEffect(() => {
    if (!open) return;
    setResolved('yes');
    setJustification('');
    setEvidenceSummary('');
  }, [open]);

  const followUp = useMemo(
    () => computeFollowUpWindow({ now: now ?? new Date(), timeZone }),
    [now, timeZone],
  );

  const rejecting = resolved === 'no';
  const missingJustification = rejecting && justification.trim().length === 0;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title="Verificação de eficácia"
      description="A ação resolveu o problema? A resposta é ato assinado eletronicamente."
      footer={
        <>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button
            variant={rejecting ? 'danger-outline' : 'primary'}
            disabled={missingJustification}
            aria-label={
              missingJustification
                ? 'Continuar para a assinatura — indisponível. Informe a justificativa quando a ação não resolveu o problema.'
                : undefined
            }
            onClick={() =>
              onConfirm({
                approved: !rejecting,
                justification: justification.trim(),
                evidenceSummary: evidenceSummary.trim(),
              })
            }
          >
            Continuar para a assinatura
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <RadioGroup<'yes' | 'no'>
          legend="A ação resolveu o problema?"
          value={resolved}
          onValueChange={setResolved}
          options={[
            {
              value: 'yes',
              label: 'Resolveu',
              description:
                'A ação foi eficaz. Ela é concluída e deixa de bloquear o encerramento da não-conformidade.',
            },
            {
              value: 'no',
              label: 'Não resolveu',
              description:
                'A ação foi executada e o problema persiste. A justificativa é obrigatória neste caminho.',
            },
          ]}
        />

        {/*
          ═══════════════════════════════════════════════════════════════════
          O AVISO. `role="alert"` porque a consequência aparece DEPOIS de o
          usuário já ter escolhido "não resolveu": sem o anúncio, quem usa
          leitor de tela confirmaria sem nunca saber que uma reunião foi
          agendada em seu nome.
          ═══════════════════════════════════════════════════════════════════
        */}
        {rejecting ? (
          <div
            role="alert"
            className="rounded-md border border-warning bg-warning-subtle px-3 py-3 text-sm text-warning-content"
          >
            <p className="flex items-center gap-2 font-semibold">
              <IconAlertTriangle size={16} aria-hidden="true" />
              Reprovar tem três consequências automáticas
            </p>
            <ol className="mt-2 flex list-inside list-decimal flex-col gap-1.5">
              <li>
                Uma <strong className="font-semibold">SACAP de follow-up</strong> é criada para{' '}
                <span className="qc-tabular font-semibold">
                  {formatRegulatedDateTime(followUp.startsAt, timeZone)}
                </span>{' '}
                — D+{FOLLOW_UP_OFFSET_DAYS} às 09:00 no fuso da organização, com uma hora de
                duração. Fim de semana é empurrado para a segunda-feira seguinte.
              </li>
              <li>
                A nova reunião <strong className="font-semibold">herda</strong> as
                não-conformidades e os participantes da reunião de origem, todos com presença ainda
                não confirmada, e <strong className="font-semibold">esta ação é movida</strong> para
                ela.
              </li>
              <li>
                A ação é <strong className="font-semibold">reaberta</strong> e volta a bloquear o
                encerramento da não-conformidade até que uma nova verificação a aprove.
              </li>
            </ol>
            <p className="mt-2">
              Tudo isso acontece numa única transação, junto com a sua assinatura. Não há como
              reprovar sem gerar o follow-up.
            </p>
          </div>
        ) : (
          <p className="flex items-start gap-2 rounded-md border border-info bg-info-subtle px-3 py-2.5 text-sm text-info-content">
            <IconInfo size={15} aria-hidden="true" className="mt-0.5 shrink-0" />
            <span>
              Aprovar conclui a ação como eficaz. Quando todas as ações da não-conformidade
              estiverem aprovadas, o encerramento fica liberado.
            </span>
          </p>
        )}

        <Field
          label="Como a eficácia foi comprovada"
          description="Indicador, reinspeção, auditoria de seguimento, ausência de recorrência no período. ISO 13485 §8.5.2 pede a evidência, não só a opinião."
        >
          <Textarea
            value={evidenceSummary}
            onChange={(event) => setEvidenceSummary(event.target.value)}
            rows={4}
            maxLength={4000}
            showCounter
          />
        </Field>

        <Field
          label="Justificativa"
          required={rejecting}
          error={
            missingJustification && justification.length > 0
              ? 'Informe a justificativa quando a ação não resolveu o problema.'
              : undefined
          }
          description={
            rejecting
              ? 'Obrigatória na reprovação. O texto entra no diário de bordo da ação, prefixado como reabertura, e na pauta da SACAP de follow-up.'
              : 'Opcional na aprovação.'
          }
        >
          <Textarea
            value={justification}
            onChange={(event) => setJustification(event.target.value)}
            rows={4}
            maxLength={4000}
            showCounter
          />
        </Field>

        <p className="flex items-start gap-2 text-xs text-content-subtle">
          <IconUsers size={14} aria-hidden="true" className="mt-0.5 shrink-0" />
          <span>
            Responsável pela execução: {assigneeName ?? '—'}. Solicitante e verificador:{' '}
            {requesterName ?? '—'}. A autoverificação é bloqueada por padrão — segregação de
            funções.
          </span>
        </p>
      </div>
    </Dialog>
  );
}
